import { Box, Button, CircularProgress, Typography } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { getAPIErrorMessage } from '../../../helpers/helpers';
import {
  type ConnectionMode,
  ThirdPartyIntegrationService,
} from '../../../service/ThirdPartyIntegrationService';
import {
  NotificationType,
  useNotificationStore,
} from '../../../store/storeNotifications';
import { useStoreThirdPartyIntegration } from '../../../store/storeThirdPartyIntegration';
import CredentialsForm from '../../ThirdPartyIntegration/common/CredentialsForm';
import ConnectToProvider from '../../ThirdPartyIntegration/oauth/ConnectToProvider';
import type { IntegrationProviderConfig } from '../../ThirdPartyIntegration/types';

interface AddConnectionPanelProps {
  config: IntegrationProviderConfig;
  isDark: boolean;
  hasExistingConnections: boolean;
}

const AddConnectionPanel: React.FC<AddConnectionPanelProps> = ({
  config,
  isDark,
  hasExistingConnections,
}) => {
  const setNotification = useNotificationStore(state => state.setNotification);
  const { fetchConnections } = useStoreThirdPartyIntegration();
  const [connectionMode, setConnectionMode] = useState<ConnectionMode | null>(
    null,
  );
  const [loadingMode, setLoadingMode] = useState(false);
  const [isConnectingWebhook, setIsConnectingWebhook] = useState(false);
  const [expanded, setExpanded] = useState(!hasExistingConnections);

  useEffect(() => {
    if (!config.id) return;
    let cancelled = false;
    setLoadingMode(true);
    ThirdPartyIntegrationService.getConnectionMode(config.id)
      .then(res => {
        if (!cancelled && res?.data?.connectionMode) {
          setConnectionMode(res.data.connectionMode as ConnectionMode);
        }
      })
      .catch(() => {
        if (!cancelled) setConnectionMode(null);
      })
      .finally(() => {
        if (!cancelled) setLoadingMode(false);
      });
    return () => {
      cancelled = true;
    };
  }, [config.id]);

  const handleWebhookConnect = useCallback(async () => {
    setIsConnectingWebhook(true);
    try {
      const response = await ThirdPartyIntegrationService.connect(
        config.id,
        {},
      );
      const data = response?.data as { integrationId?: string } | undefined;
      if (data?.integrationId) {
        await fetchConnections(config.id);
        setNotification({
          type: NotificationType.Success,
          message: `Connected to ${config.name}.`,
        });
      } else {
        setNotification({
          type: NotificationType.Error,
          message: 'No integration ID returned',
        });
      }
    } catch (error) {
      setNotification({
        type: NotificationType.Error,
        message: getAPIErrorMessage(error),
      });
    } finally {
      setIsConnectingWebhook(false);
    }
  }, [config.id, config.name, fetchConnections, setNotification]);

  const titleColor = isDark ? '#F9FAFB' : '#101828';
  const subtitleColor = isDark ? '#98A2B3' : '#667085';
  const brandColor = config.brandColor ?? (isDark ? '#7F56D9' : '#6941C6');

  const showOAuth =
    connectionMode === 'oauth' ||
    (connectionMode === null && (config.authFields?.length ?? 0) === 0);
  const showCredentials =
    connectionMode === 'credentials' ||
    (connectionMode === null && (config.authFields?.length ?? 0) > 0);
  const showWebhook = connectionMode === 'webhook';

  if (loadingMode) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
        <CircularProgress size={14} />
        <Typography sx={{ fontSize: '12px', color: subtitleColor }}>
          Loading connection options…
        </Typography>
      </Box>
    );
  }

  if (hasExistingConnections && !expanded) {
    return (
      <Button
        variant='outlined'
        size='small'
        onClick={() => setExpanded(true)}
        sx={{
          textTransform: 'none',
          fontSize: '12px',
          borderColor: brandColor,
          color: brandColor,
          '&:hover': {
            borderColor: brandColor,
            backgroundColor: `${brandColor}10`,
          },
        }}
      >
        Add another connection
      </Button>
    );
  }

  return (
    <Box
      sx={{
        border: isDark
          ? '1px solid rgba(71, 84, 103, 0.4)'
          : '1px solid #EAECF0',
        borderRadius: '8px',
        padding: 2,
      }}
    >
      <Typography
        sx={{
          fontSize: '13px',
          fontWeight: 600,
          color: titleColor,
          mb: 1.5,
        }}
      >
        New {config.name} connection
      </Typography>
      {showOAuth && (
        <ConnectToProvider
          provider={config.id}
          onSuccess={() => {
            fetchConnections(config.id);
            if (hasExistingConnections) setExpanded(false);
          }}
        >
          {({ onClick, isConnecting }) => (
            <Button
              variant='contained'
              size='small'
              disabled={isConnecting}
              onClick={onClick}
              sx={{
                backgroundColor: brandColor,
                color: '#fff',
                textTransform: 'none',
                fontSize: '12px',
                fontWeight: 600,
                width: '100%',
                '&:hover': {
                  filter: 'brightness(0.88)',
                  backgroundColor: brandColor,
                },
              }}
            >
              {isConnecting ? 'Connecting…' : `Connect to ${config.name}`}
            </Button>
          )}
        </ConnectToProvider>
      )}
      {showCredentials && (
        <CredentialsForm
          provider={config.id}
          providerName={config.name}
          fields={config.authFields ?? []}
          onSuccess={() => {
            fetchConnections(config.id);
            if (hasExistingConnections) setExpanded(false);
          }}
        />
      )}
      {showWebhook && (
        <Button
          variant='contained'
          size='small'
          disabled={isConnectingWebhook}
          onClick={handleWebhookConnect}
          sx={{
            backgroundColor: brandColor,
            color: '#fff',
            textTransform: 'none',
            fontSize: '12px',
            fontWeight: 600,
            width: '100%',
            '&:hover': {
              filter: 'brightness(0.88)',
              backgroundColor: brandColor,
            },
          }}
        >
          {isConnectingWebhook ? 'Connecting…' : `Connect to ${config.name}`}
        </Button>
      )}
      {connectionMode === null && (
        <Typography sx={{ fontSize: '12px', color: '#F97066', mt: 1 }}>
          Provider not supported or unavailable.
        </Typography>
      )}
      {hasExistingConnections && (
        <Button
          size='small'
          onClick={() => setExpanded(false)}
          sx={{
            textTransform: 'none',
            fontSize: '11px',
            mt: 1,
            color: subtitleColor,
          }}
        >
          Cancel
        </Button>
      )}
    </Box>
  );
};

export default AddConnectionPanel;
